if (!customElements.get('delivery-step')) {
	class DeliveryStep extends HTMLElement {
		connectedCallback() {
			this._updateContent();
			this.executeToday = this.todayExecutionWithCutOffTime;
		}
		get minOffset() {
			if (this.todayExecutionWithCutOffTime) return 0;
			if (this.lastStep) {
				return Math.max(this.offsets[0] - this.lastStep.maxOffset, 0);
			}
			return this.offsets[0];
		}
		get maxOffset() {
			if (this.todayExecutionWithCutOffTime) return 0;
			if (this.lastStep) {
				return Math.max(this.offsets[1] - this.lastStep.maxOffset);
			}
			return this.offsets[1];
		}
		get offsets() {
			return JSON.parse(this.getAttribute('offsets'));
		}
		get vacationDays() {
			return JSON.parse(this.getAttribute('vacation-days'));
		}
		get cutOffTime() {
			return this.getAttribute('cut-off-time');
		}
		get todayExecutionWithCutOffTime() {
			if (this.getAttribute('same-day-execution-with-cut-off-time') === null) return false;
			const dayWithTodayExecution = JSON.parse(this.getAttribute('same-day-execution-with-cut-off-time')).map(this._convertWeekdayNamesToNumber) || [];
			if (dayWithTodayExecution.length === 0) return false;
			if (this._isAfterCutOffCurrentTime()) return false;
			const now = new Date();
			if (dayWithTodayExecution.includes(now.getDay())) {
				this.setAttribute('offsets', '[0, 0]');
				return true;
			}
		}
		get weekendDays() {
			const weekendDays = JSON.parse(this.getAttribute('weekend-days'))
				? JSON.parse(this.getAttribute('weekend-days'))
				: [];
			return weekendDays.map(this._convertWeekdayNamesToNumber);
		}
		get startingDate() {
			if (this.lastStep) {
				return this.lastStep.maxDate;
			}
			return this.getAttribute('starting-date');
		}
		get ignoreCutOffTime() {
			return this.getAttribute('ignore-cut-off-time') === 'true';
		}
		get lastStep() {
			if (this.getAttribute('last-step-id')) {
				return document.getElementById(this.getAttribute('last-step-id'));
			}
			return null;
		}
		get getIgnoreVacationsAndWeekends() {
			return this.getAttribute('ignore-vacation') === 'true';
		}
		set minDate(date) {
			this.setAttribute('min-date', date);
		}
		set maxDate(date) {
			this.setAttribute('max-date', date);
		}
		get minDate() {
			return this.getAttribute('min-date');
		}
		get maxDate() {
			return this.getAttribute('max-date');
		}
		set ignoreCutOffTime(value) {
			this.setAttribute('ignore-cut-off-time', value.toString());
		}
		_convertWeekdayNamesToNumber(weekday) {
			const weekdays = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
			return weekdays.indexOf(weekday.toLowerCase());
		}
		_formatDate(minDate, maxDate) {
			const minDay = minDate.toLocaleString('default', { day: '2-digit' });
			// Use numeric month with two digits instead of short month name (e.g., 10 -> "10")
			const minMonth = minDate.toLocaleString('default', { month: '2-digit' });
			const maxDay = maxDate.toLocaleString('default', { day: '2-digit' });
			const maxMonth = maxDate.toLocaleString('default', { month: '2-digit' });
			if (this.minOffset !== this.maxOffset && minMonth === maxMonth) {
				return theme.deliveryStrings.deliveryDateSameMonth
					.replace('{{day}}', minDay)
					// Replace all occurrences of {{month}} (appears twice in same-month template)
					.replace(/\{\{month\}\}/g, minMonth)
					.replace('{{max_day}}', maxDay);
			} else if (this.minOffset !== this.maxOffset) {
				return theme.deliveryStrings.deliveryDatesDiffMonth
					.replace('{{max_day}}', maxDay)
					.replace('{{max_month}}', maxMonth)
					.replace('{{day}}', minDay)
					.replace('{{month}}', minMonth);
			} else {
				return theme.deliveryStrings.deliveryDateOneDate.replace('{{day}}', minDay).replace('{{month}}', minMonth);
			}
		}
		_setCutoffIgnore() {
			const now = this.startingDate ? new Date(this.startingDate) : new Date();
			if (this.cutOffDays && !this.cutOffDays.includes(now.getDay())) {
				this.ignoreCutOffTime = true;
			}
		}
		_addDaysSkippingWeekendsAndVacations(daysToAdd) {
			if (this.getIgnoreVacationsAndWeekends) {
				return new Date();
			}
			let currentDate = this.startingDate ? new Date(this.startingDate) : new Date();
			if (this._isStartingDateWeekendOrVacation()) {
				currentDate = this._getFirstNonWeekendOrVacationDay();
				if (this.lastStep.getIgnoreVacationsAndWeekends) return currentDate;
			}
			let addedDays = 0;
			while (addedDays < daysToAdd) {
				currentDate.setDate(currentDate.getDate() + 1);
				if (this.weekendDays.includes(currentDate.getDay())) continue;

				const dateString = currentDate.toISOString().split('T')[0];
				if (this.vacationDays.includes(dateString)) continue;
				addedDays++;
			}
			return currentDate;
		}
		_isStartingDateWeekendOrVacation() {
			const startingDate = this.startingDate ? new Date(this.startingDate) : new Date();
			const dateString = startingDate.toISOString().split('T')[0];
			return this.vacationDays.includes(dateString) || this.weekendDays.includes(startingDate.getDay());
		}
		_getFirstNonWeekendOrVacationDay() {
			let currentDate = this.startingDate ? new Date(this.startingDate) : new Date();
			while (true) {
				currentDate.setDate(currentDate.getDate() + 1);
				if (this.weekendDays.includes(currentDate.getDay())) continue;
				const dateString = currentDate.toISOString().split('T')[0];
				if (this.vacationDays.includes(dateString)) continue;
				break;
			}
			return currentDate;
		}
		_isAfterCutOffCurrentTime() {
			const now = new Date();
			const hours = now.getHours();
			const minutes = now.getMinutes();

			const currentTimeFormatted = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;

			return currentTimeFormatted > this.cutOffTime;
		}
		_isAfterCutOffTime() {
			const now = this.startingDate ? new Date(this.startingDate) : new Date();

			const hours = now.getHours();
			const minutes = now.getMinutes();

			const currentTimeFormatted = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;

			return currentTimeFormatted > this.cutOffTime;
		}
		_updateContent() {
			const cutOffOffset = this.ignoreCutOffTime ? 0 : this._isAfterCutOffTime() ? 1 : 0;
			const minAddedDays = this.minOffset + cutOffOffset;
			const maxAddedDays = this.maxOffset + cutOffOffset;
			const minDate = this._addDaysSkippingWeekendsAndVacations(minAddedDays);
			const maxDate = this._addDaysSkippingWeekendsAndVacations(maxAddedDays);
			this.minDate = minDate.toISOString().split('T')[0];
			this.maxDate = maxDate.toISOString().split('T')[0];
			this.innerHTML = this._formatDate(minDate, maxDate);
		}
	}
	window.customElements.define('delivery-step', DeliveryStep);
}
